import {
  Component, Input, OnInit, OnChanges,
  SimpleChanges, SecurityContext, ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {DomSanitizer, SafeHtml, SafeResourceUrl, SafeUrl} from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import {ExcTranslation} from 'exc-core-v2';

// npm install emailjs-mime-parser emailjs-mime-codec
// npm install --save-dev @types/node
declare const require: any;

export interface EmlAttachment {
  filename: string;
  contentType: string;
  size: number;
  data: Uint8Array;
  objectUrl?: string;
  safeUrl: SafeUrl;
  contentId?: string;
}

export interface ParsedEmail {
  from: string;
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  date: string;
  htmlBody: SafeHtml | null;
  htmlBodyRaw?: string | null;
  textBody: string | null;
  attachments: EmlAttachment[];
}

@Component({
  selector: 'app-eml-viewer',
  templateUrl: './eml-viewer.component.html',
  styles: [
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmlViewerComponent extends ExcTranslation implements OnInit, OnChanges {

  /** URL des REST-Endpoints, der die rohe EML zurückliefert */
  @Input() emlUrl: string | null = null;

  /** Oder direkt den EML-Rohtext übergeben */
  @Input() emlRaw: string | null = null;

  email: ParsedEmail | null = null;
  isLoading = false;
  error: string | null = null;
  activeTab: 'html' | 'text' = 'html';

  constructor(
    private sanitizer: DomSanitizer,
    private http: HttpClient,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.emlUrl || changes.emlRaw) {
      this.load();
    }
  }

  private async load(): Promise<void> {
    this.email = null;
    this.error = null;

    if (this.emlRaw) {
      const raw = this.emlRaw;
      this.isLoading = true;
      this.cdr.markForCheck();
      setTimeout(() => this.parse(raw), 0);
    } else if (this.emlUrl) {
      this.isLoading = true;
      this.cdr.markForCheck();
      this.http.get(this.emlUrl, { responseType: 'text' }).subscribe({
        next: (raw) => { this.parse(raw); },
        error: (err) => {
          this.isLoading = false;
          this.error = `Fehler beim Laden: ${err.message ?? err.status}`;
          this.cdr.markForCheck();
        }
      });
    }
  }

  private async parse(raw: string): Promise<void> {
    try {
      // Dynamic import – muss im Projekt installiert sein:
      // npm install emailjs-mime-parser emailjs-mime-codec
      // @ts-ignore
      const { default: parse } = await import('emailjs-mime-parser');
      const node = parse(raw);

      this.email = {
        from:    this.getHeader(node, 'from'),
        to:      this.getHeader(node, 'to'),
        cc:      this.getHeader(node, 'cc'),
        bcc:     this.getHeader(node, 'bcc'),
        subject: this.decodeWords(this.getHeader(node, 'subject')),
        date:    this.getHeader(node, 'date'),
        htmlBody: null,
        htmlBodyRaw: null,
        textBody: null,
        attachments: []
      };

      this.walkNode(node, this.email);

      if (this.email.htmlBodyRaw) {
        this.email.htmlBody = this.sanitizer.bypassSecurityTrustHtml(
          this.normalizeHtmlLayout(
            this.resolveCidImages(this.email.htmlBodyRaw, this.email)
          )
        ) as SafeHtml;
      }

      if (this.email.htmlBody) { this.activeTab = 'html'; }
      else { this.activeTab = 'text'; }

    } catch (e: any) {
      this.error = `Parse-Fehler: ${e?.message ?? 'Unbekannt'}`;
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  private walkNode(node: any, email: ParsedEmail): void {
    const ct: string = node?.contentType?.value ?? '';
    const disp: string = node?.headers?.['content-disposition']?.[0]?.value ?? '';

    // Multipart → Kinder rekursiv durchlaufen
    if (ct.startsWith('multipart/')) {
      (node.childNodes ?? []).forEach((child: any) => this.walkNode(child, email));
      return;
    }

    const isAttachment = disp.startsWith('attachment') ||
      (node?.headers?.['content-disposition']?.[0]?.params?.filename);

    if (isAttachment) {
      const filename =
        node?.headers?.['content-disposition']?.[0]?.params?.filename ||
        node?.contentType?.params?.name ||
        'anhang';
      const bytes: Uint8Array = node.content ?? new Uint8Array();
      const blob = new Blob([bytes], { type: ct || 'application/octet-stream' });
      const objectUrl = URL.createObjectURL(blob);
      const contentIdRaw = node?.headers?.['content-id']?.[0]?.value ?? '';
      const contentId = contentIdRaw.replace(/[<>]/g, '');
      email.attachments.push({
        filename: this.decodeWords(filename),
        contentType: ct,
        size: bytes.byteLength,
        data: bytes,
        objectUrl,
        safeUrl: this.sanitizer.bypassSecurityTrustUrl(objectUrl),
        contentId
      });
      return;
    }

    if (ct === 'text/html' && !email.htmlBody) {
      email.htmlBodyRaw = this.decodeContent(node);
    }

    if (ct === 'text/plain' && !email.textBody) {
      email.textBody = this.decodeContent(node);
    }
  }

  private decodeContent(node: any): string {
    try {
      const charset: string = node?.contentType?.params?.charset ?? 'utf-8';
      if (node.content instanceof Uint8Array) {
        return new TextDecoder(charset).decode(node.content);
      }
      return String(node.content ?? '');
    } catch {
      return String(node.content ?? '');
    }
  }

  private getHeader(node: any, name: string): string {
    const h = node?.headers?.[name];
    if (!h) { return ''; }
    const raw = h[0]?.initial ?? h[0]?.value ?? '';
    return this.decodeWords(raw);
  }

  private decodeWords(str: string): string {
    if (!str) { return ''; }
    // RFC 2047 encoded words: =?charset?encoding?text?=
    return str.replace(/=\?([^?]+)\?([BbQq])\?([^?]*)\?=/g, (_, charset, enc, text) => {
      try {
        if (enc.toUpperCase() === 'B') {
          const bin = atob(text);
          const bytes = Uint8Array.from(bin, c => c.charCodeAt(0));
          return new TextDecoder(charset).decode(bytes);
        } else {
          const decoded = text.replace(/_/g, ' ').replace(/=([0-9A-Fa-f]{2})/g,
            // tslint:disable-next-line:no-shadowed-variable
            (_: string, h: string) => String.fromCharCode(parseInt(h, 16)));
          // const bytes = Uint8Array.from(decoded, c => c.charCodeAt(0));
          const bytes = new Uint8Array([...decoded].map(c => c.charCodeAt(0)));

          return new TextDecoder(charset).decode(bytes);
        }
      } catch { return str; }
    });
  }

  private resolveCidImages(html: string, email: ParsedEmail): string {
    return html
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/\s(on\w+)=(".*?"|'.*?'|[^\s>]+)/gi, '')
      .replace(/cid:([^"'\s>]+)/gi, (_: string, cid: string) => {
        const cleanCid = cid.replace(/[<>]/g, '').trim();
        const match = email.attachments.find(a => a.contentId === cleanCid);

        if (match && match.data) {
          const binary = Array.from(match.data).map(byte => String.fromCharCode(byte)).join('');
          const base64 = btoa(binary);
          return `data:${match.contentType};base64,${base64}`;
        }

        return '';
      });
  }

  private normalizeHtmlLayout(html: string): string {
    const layoutStyle = `
    <style>
      html, body {
        margin: 0;
        padding: 0;
        max-width: 100%;
        overflow-x: hidden;
        box-sizing: border-box;
      }
      img {
        max-width: 100% !important;
        height: auto !important;
      }
      table {
        max-width: 100% !important;
        width: auto !important;
      }
      td, div, p, span {
        max-width: 100%;
        box-sizing: border-box;
        word-break: break-word;
      }
    </style>
  `;

    if (/<head[^>]*>/i.test(html)) {
      return html.replace(/<head([^>]*)>/i, `<head$1>${layoutStyle}`);
    }

    return layoutStyle + html;
  }

  formatSize(bytes: number): string {
    if (bytes < 1024) { return `${bytes} B`; }
    if (bytes < 1024 * 1024) { return `${(bytes / 1024).toFixed(1)} KB`; }
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  getFileIcon(contentType: string): string {
    if (contentType.startsWith('image/')) {       return '🖼️'; }
    if (contentType.includes('pdf')) {            return '📄'; }
    if (contentType.includes('zip') || contentType.includes('archive')) { return '📦'; }
    if (contentType.includes('word') || contentType.includes('document')) { return '📝'; }
    if (contentType.includes('sheet') || contentType.includes('excel')) {   return '📊'; }
    if (contentType.startsWith('text/')) {        return '📃'; }
    return '📎';
  }
}
